import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { AlertTriangle, ArrowLeft, RotateCw, Users } from 'lucide-react'
import Loader from '../../../components/Loader'
import ProfileLink from '../../../components/ProfileLink'
import useAudioManager from '../../../hooks/useAudioManager'
import { hapticImpact } from '../../../utils/haptic'
import eventApi from '../services/eventApi'
import TeamProgressCard from '../components/halloween/TeamProgressCard'
import LadderCard from '../components/halloween/LadderCard'
import { CARD_CLASS, PumpkinIcon, SECTIONS_VARIANT, SECTION_VARIANT, SectionTitle } from '../components/halloween/shared'

// Тыквы пересчитываются раз в минуту — карточку банды обновляем не чаще
const POLL_MS = 60000

const PlayerRow = ({ player, showToday }) => {
  const { t } = useTranslation()
  return (
    <div
      className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
        player.is_you ? 'border-orange-400/40 bg-orange-500/10' : 'border-white/5 bg-black/20'
      } ${player.in_team ? '' : 'opacity-60'}`}
    >
      <ProfileLink slug={player.slug} className="shrink-0">
        {player.ava ? (
          <img src={player.ava} alt="" className={`h-9 w-9 rounded-full object-cover ${player.in_team ? '' : 'grayscale'}`} />
        ) : (
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-sm font-bold text-white">
            {(player.name || '?').slice(0, 1).toUpperCase()}
          </span>
        )}
      </ProfileLink>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5">
          <ProfileLink slug={player.slug} className="truncate text-sm font-bold text-white">
            {player.name}
          </ProfileLink>
          {player.is_you && (
            <span className="shrink-0 rounded-md bg-orange-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase text-orange-200">
              {t('halloweenEvent.teamPage.you')}
            </span>
          )}
          {!player.in_team && (
            <span className="shrink-0 rounded-md bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-text-secondary">
              {t('halloweenEvent.teamPage.left')}
            </span>
          )}
        </p>
        {showToday && player.today_pumpkins > 0 && (
          <p className="text-[11px] text-orange-200/80">{t('halloweenEvent.teamPage.today', { count: player.today_pumpkins })}</p>
        )}
      </div>
      <span className="flex shrink-0 items-center gap-1 text-sm font-black tabular-nums text-orange-100">
        <PumpkinIcon size={16} />
        {player.pumpkins}
      </span>
    </div>
  )
}

/**
 * Банда в эвенте (GET /api/event/halloween/team/{slug}/): прогресс, лестница
 * и игроки, принёсшие тыквы. Открыта всем по любой банде; 404 — банды нет,
 * { enabled: false } — эвента нет, уводим на главную.
 */
const HalloweenTeamPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { slug } = useParams()
  const { playSound } = useAudioManager({ select: 'select' })

  const [data, setData] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const mountedRef = useRef(true)

  const loadTeam = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true)
      setError(null)
    }
    try {
      const response = await eventApi.getHalloweenTeam(slug)
      if (!mountedRef.current) return
      setData(response)
      setError(null)
    } catch (err) {
      if (!mountedRef.current || err?.name === 'AbortError') return
      if (!silent) setError(err?.status === 404 ? 'notFound' : 'load')
    } finally {
      if (mountedRef.current && !silent) setIsLoading(false)
    }
  }, [slug])

  useEffect(() => {
    mountedRef.current = true
    setData(null)
    loadTeam()
    return () => {
      mountedRef.current = false
    }
  }, [loadTeam])

  // Обновляем, пока идёт сбор тыкв; после него вклад игроков не меняется
  useEffect(() => {
    if (data?.phase !== 'active') return undefined
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') loadTeam({ silent: true })
    }, POLL_MS)
    return () => clearInterval(interval)
  }, [data?.phase, loadTeam])

  useEffect(() => {
    if (data?.enabled === false) navigate('/', { replace: true })
  }, [data, navigate])

  const go = (path) => {
    playSound('select')
    hapticImpact('light')
    navigate(path)
  }

  const backButton = (
    <button
      type="button"
      onClick={() => go('/event')}
      className="mb-4 inline-flex items-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-sm font-semibold text-white transition-all hover:bg-white/10 active:scale-[0.98]"
    >
      <ArrowLeft size={16} className="rtl:rotate-180" />
      {t('halloweenEvent.title')}
    </button>
  )

  if (isLoading && !data) return <Loader message="" />

  if (error && !data) {
    return (
      <div className="px-4 pt-4 lg:px-0 lg:pt-6">
        {backButton}
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-red-500/10">
            <AlertTriangle size={28} className="text-red-400" />
          </div>
          <p className="text-sm text-text-secondary">
            {error === 'notFound' ? t('halloweenEvent.teamPage.notFound') : t('halloweenEvent.error')}
          </p>
          {error !== 'notFound' && (
            <button
              type="button"
              onClick={() => loadTeam()}
              className="hev-cta flex items-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold transition-transform active:scale-[0.98]"
            >
              <RotateCw size={16} />
              {t('halloweenEvent.retry')}
            </button>
          )}
        </div>
      </div>
    )
  }

  if (!data || data.enabled === false) return null

  const { team, ladder = [], players = [], phase } = data

  return (
    <div className="px-4 pb-10 pt-4 text-white lg:px-0 lg:pt-6">
      <div className="mx-auto w-full lg:max-w-5xl">
        {backButton}
        <motion.div
          variants={SECTIONS_VARIANT}
          initial="hidden"
          animate="visible"
          className="space-y-4 lg:grid lg:grid-cols-12 lg:items-start lg:gap-6 lg:space-y-0"
        >
          <div className="space-y-4 lg:col-span-5">
            <motion.div variants={SECTION_VARIANT}>
              <TeamProgressCard
                team={team}
                onOpen={() => go(`/teams/${encodeURIComponent(team.id)}`)}
                actionLabel={t('halloweenEvent.teamPage.openTeam')}
                badge={team.is_mine ? (
                  <span className="rounded-lg border border-orange-400/40 bg-orange-500/15 px-2 py-0.5 text-[11px] font-bold text-orange-200">
                    {t('halloweenEvent.teamPage.mine')}
                  </span>
                ) : null}
              />
            </motion.div>
            <motion.div variants={SECTION_VARIANT}>
              <LadderCard ladder={ladder} nextStep={team.next_step} minContributors={team.contributors_needed} />
            </motion.div>
          </div>

          <motion.section variants={SECTION_VARIANT} className={`${CARD_CLASS} p-5 lg:col-span-7`}>
            <SectionTitle icon={<Users size={18} className="text-orange-300" />}>
              {t('halloweenEvent.teamPage.players')}
            </SectionTitle>
            {players.length === 0 ? (
              <p className="mt-4 text-sm text-text-secondary">{t('halloweenEvent.teamPage.empty')}</p>
            ) : (
              <div className="mt-4 space-y-1.5">
                {players.map((player, index) => (
                  <PlayerRow key={player.slug || `${player.name}-${index}`} player={player} showToday={phase === 'active'} />
                ))}
              </div>
            )}
          </motion.section>
        </motion.div>
      </div>
    </div>
  )
}

export default HalloweenTeamPage
